import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { SelectChangeEvent } from "@mui/material";
import {
  answersActions,
  RootStateAnswers,
} from "../../../questionnaire/store/answers-slice";
import {
  EditBiometricQuestionnaire,
  EditCustomerPersonalInfo,
} from "../../../../common/network/Api";
import { useAppStore } from "../../../../providers/AppStore";
import { validateText } from "../../../../providers/helperFunctions";
import { getStoredPhoneValidity } from "../../../../common/utils/phone";
import { cmToIn, inToCm, kgToPd, pdToKg } from "../../../questionnaire/utils";
import { biometricInfoType, personalInfoType } from "../../store/helper";
import { useAuth } from "../../../../providers/AuthProvider";
import { useSubject } from "../../providers/SubjectProvider";
import {
  isResourcePending,
  usePlatformResource,
} from "../../hooks/usePlatformResource";

/**
 * The Profile screen's shared form machinery.
 *
 * Lifted out of the old `BiometricTab` because the redesign splits one form
 * across two tabs (Basic information, Onboarding) while Save/Cancel live in a
 * single sticky footer owned by the screen. The fields are in route children,
 * the buttons are in the parent — so the state has to sit above both.
 *
 * Everything here is behaviour-preserving: the same two save calls, the same
 * validation gates, the same unit conversion, the same dirty tracking. Only the
 * rendering moved.
 */

export type ProfileTabKey =
  | "basic-information"
  | "onboarding"
  | "nutrition-questionnaire";

type ProfileFormContextValue = {
  // Unit toggle (Metric/Imperial) — owned here because it renders in the
  // screen's tab-strip row but converts values used by the Onboarding tab.
  massUnit: number;
  onUnitChange: (event: SelectChangeEvent<unknown>) => void;

  // Shared single-open popover discipline across every field on the page.
  openPopperId: string | null;
  setOpenPopperId: React.Dispatch<React.SetStateAction<string | null>>;

  // InBody editing buffer (Basic information → Integration card).
  inBodyValue: string;
  inBodyDirty: boolean;
  onInBodyChange: (value: string) => void;

  // Address completeness is reported upward by the autocomplete field.
  setAddressMissingParts: React.Dispatch<React.SetStateAction<string[]>>;
  addressMissingParts: string[];
  phoneInvalid: boolean;

  // Per-tab dirty flags — drive both the tab-strip dots and the footer copy.
  isBasicInformationDirty: boolean;
  isOnboardingDirty: boolean;
  /** Every dirty tab, in tab order. Empty when the form is clean. */
  dirtyTabs: ProfileTabKey[];

  isSendingData: boolean;
  /** True until the biometric questionnaire has loaded — tabs show a loader. */
  isLoadingQuestionnaire: boolean;
  handleSave: () => Promise<void>;
  handleCancel: () => Promise<void>;
};

const ProfileFormContext = createContext<ProfileFormContextValue | null>(null);

/**
 * Biometric-questionnaire fields that the Basic information tab renders
 * (`BasicInformationFields` with `includeBiometricFields`). Their data saves
 * through the questionnaire, but the dirty indicator has to point at the tab
 * the user was actually editing.
 */
const BASIC_INFO_BIOMETRIC_FIELDS: string[] = [
  biometricInfoType.gender,
  biometricInfoType.birth_date,
];

/** Shallow equality over two flat answer objects (same helper as before). */
const checkHaveSameData = (obj1: any, obj2: any) => {
  const obj1Length = Object.keys(obj1).length;
  const obj2Length = Object.keys(obj2).length;

  if (obj1Length === obj2Length) {
    return Object.keys(obj1).every(
      (key) => obj2.hasOwnProperty(key) && obj2[key] === obj1[key]
    );
  }
  return false;
};

export const ProfileFormProvider = ({ children }: { children: ReactNode }) => {
  const dispatch = useDispatch();
  const {
    ASSelectedClient,
    ASSetSelectedClient,
    ASSetOpenSnackBar,
    ASSetSnackBarMessage,
    ASSetTypeSnackBar,
    ASGetCustomerPersonalInfo,
    ASRefreshResource,
  } = useAppStore();

  const clientData = useSelector((state: RootStateAnswers) => state.answers);

  // Basic information and Onboarding both edit the biometric questionnaire, so
  // the shared form context declares it once for both tabs.
  const { subjectId, isSelf } = useSubject();
  const { AuthUser } = useAuth();
  const biometricStatus = usePlatformResource(
    "biometricQuestionnaire",
    subjectId,
    AuthUser.role
  );

  // Contact details. In the client workspace loadClientData has already put
  // them in the slice as part of resolving the client's identity; a customer
  // viewing their own Profile has no such step, so declare it here. Passing a
  // null id in the workspace makes the hook a no-op rather than a second fetch.
  const personalInfoStatus = usePlatformResource(
    "personalInfo",
    isSelf ? subjectId : null,
    AuthUser.role
  );

  const [isSendingData, setIsSendingData] = useState(false);
  const [openPopperId, setOpenPopperId] = useState<string | null>(null);
  const [inBodyValue, setInBodyValue] = useState("");
  const [inBodyDirty, setInBodyDirty] = useState(false);
  const [addressMissingParts, setAddressMissingParts] = useState<string[]>([]);

  // Same verdict the field hint shows. An empty phone (or bare dial code) is
  // "not given", not "wrong", so only an explicit `false` blocks Save.
  const phoneInvalid =
    getStoredPhoneValidity(
      clientData.personalInfoAnswers.phone,
      clientData.personalInfoAnswers[personalInfoType.countryCode]
    ) === false;
  // A missing street number only warns; a missing postal code blocks Save.
  const hasValidationError =
    phoneInvalid || addressMissingParts.includes("postal code");

  // Two sources because the two modes store it differently: the workspace hangs
  // it on the selected client, self mode carries it in the profile answers.
  const inBodyFromSubject =
    ASSelectedClient?.InBodyAccount ??
    clientData.personalInfoAnswers.inBodyAccount ??
    "";

  useEffect(() => {
    if (!inBodyDirty) setInBodyValue(inBodyFromSubject);
  }, [inBodyFromSubject, inBodyDirty]);

  const massUnit = clientData.biometricQuest.myAnswers.mass_measurement_unit;

  // Which biometric-questionnaire answers actually changed. Compared per key
  // rather than as a whole (the old `checkHaveSameData` test) because the two
  // tabs have to be attributed separately. NOT `biometric_modified_fields`:
  // that is hydrated from the server's *pending edits* (what drives the
  // "Updated" badges), so a client with any pending edit would open already
  // claiming unsaved changes the user never made.
  const changedBiometricFields = useMemo(() => {
    const current = clientData.biometricQuest.myAnswers ?? {};
    const pristine = clientData.biometricOldAnswers ?? {};
    return Object.keys(current).filter((key) => current[key] !== pristine[key]);
  }, [clientData.biometricQuest.myAnswers, clientData.biometricOldAnswers]);

  // Gender and date of birth live in the biometric questionnaire but are shown
  // on Basic information, so edits to them belong to that tab's indicator even
  // though they save down the questionnaire path. Attribution follows where the
  // field is RENDERED, not which slice holds it — the dot has to appear where
  // the user just typed.
  const changedOnBasicInformation = changedBiometricFields.some((field) =>
    BASIC_INFO_BIOMETRIC_FIELDS.includes(field)
  );
  const changedOnOnboarding = changedBiometricFields.some(
    (field) => !BASIC_INFO_BIOMETRIC_FIELDS.includes(field)
  );

  const isBasicInformationDirty =
    clientData.personalInfoModifiedFields.length > 0 ||
    inBodyDirty ||
    changedOnBasicInformation;
  const isOnboardingDirty = changedOnOnboarding;

  // Every dirty tab, in tab order — the save bar names them all rather than
  // just the first, so editing one tab then another cannot hide the fact that
  // Save and Cancel act on both.
  const dirtyTabs: ProfileTabKey[] = [
    ...(isBasicInformationDirty ? (["basic-information"] as const) : []),
    ...(isOnboardingDirty ? (["onboarding"] as const) : []),
  ];

  // Kept identical to the original BiometricTab implementation, including the
  // toggle-back branch that REMOVES the modified flags when the unit returns to
  // its saved value — without it, Metric → Imperial → Metric would leave the
  // form permanently dirty.
  const onUnitChange = (event: SelectChangeEvent<unknown>) => {
    const newValue = +(event.target as HTMLInputElement).value;
    const answers = clientData;

    if (
      newValue ===
      answers.biometricOldAnswers[biometricInfoType.mass_measurement_unit]
    ) {
      if (
        answers.biometric_modified_fields.indexOf(
          biometricInfoType.mass_measurement_unit
        ) !== -1
      ) {
        dispatch(
          answersActions.removeBiometricModifiedFields(
            biometricInfoType.mass_measurement_unit
          )
        );
        dispatch(
          answersActions.removeBiometricModifiedFields(
            biometricInfoType.length_measurement_unit
          )
        );
      }
    } else {
      if (
        answers.biometric_modified_fields.indexOf(
          biometricInfoType.mass_measurement_unit
        ) === -1
      ) {
        dispatch(
          answersActions.addBiometricModifiedFields(
            biometricInfoType.mass_measurement_unit
          )
        );
        dispatch(
          answersActions.addBiometricModifiedFields(
            biometricInfoType.length_measurement_unit
          )
        );
      }
    }

    dispatch(
      answersActions.updateBiometricAnswers({
        [biometricInfoType.mass_measurement_unit]: newValue,
      })
    );
    dispatch(
      answersActions.updateBiometricAnswers({
        [biometricInfoType.length_measurement_unit]: newValue,
      })
    );
    if (newValue === 2) {
      dispatch(
        answersActions.updateBiometricAnswers({
          current_weight: kgToPd(
            answers.biometricQuest.myAnswers.current_weight
          ),
          target_weight: kgToPd(answers.biometricQuest.myAnswers.target_weight),
          height: cmToIn(answers.biometricQuest.myAnswers.height),
        })
      );
    }
    if (newValue === 1) {
      dispatch(
        answersActions.updateBiometricAnswers({
          current_weight: pdToKg(
            answers.biometricQuest.myAnswers.current_weight
          ),
          target_weight: pdToKg(answers.biometricQuest.myAnswers.target_weight),
          height: inToCm(answers.biometricQuest.myAnswers.height),
        })
      );
    }
  };

  const onInBodyChange = (value: string) => {
    setInBodyValue(value);
    setInBodyDirty(true);
  };

  const handleSave = async () => {
    if (hasValidationError) {
      ASSetOpenSnackBar(true);
      ASSetSnackBarMessage({
        title: "Fix the highlighted fields",
        message: phoneInvalid
          ? "Please enter a valid phone number."
          : "Please select an address that includes a postal code.",
      });
      ASSetTypeSnackBar("alert");
      return;
    }
    try {
      if (
        clientData.biometricQuest.myAnswers.fat_loss_intensity === 0 &&
        clientData.biometricQuest.myAnswers.current_weight !==
          clientData.biometricQuest.myAnswers.target_weight
      ) {
        ASSetOpenSnackBar(true);
        ASSetSnackBarMessage({
          title: "You have missing values",
          message: "Goal timeline is a required field",
        });
        ASSetTypeSnackBar("alert");
        return;
      }

      if (!validateText(clientData.personalInfoAnswers.firstName)) {
        ASSetOpenSnackBar(true);
        ASSetSnackBarMessage({
          title: "You have invalid values",
          message: "First name is a required field",
        });
        ASSetTypeSnackBar("alert");
        return;
      }

      if (!validateText(clientData.personalInfoAnswers.lastName)) {
        ASSetOpenSnackBar(true);
        ASSetSnackBarMessage({
          title: "You have invalid values",
          message: "Last name is a required field",
        });
        ASSetTypeSnackBar("alert");
        return;
      }

      setIsSendingData(true);

      // Keyed off the subject, not the selected client: a customer editing their
      // own Profile has no selected client, and gating on one meant their
      // contact details and InBody id were silently dropped on Save.
      if (
        (clientData.personalInfoModifiedFields.length > 0 || inBodyDirty) &&
        subjectId
      ) {
        await EditCustomerPersonalInfo(subjectId, {
          ...clientData.personalInfoAnswers,
          inBodyAccount: inBodyValue,
          // Send the location IDs as null so the backend resolves country/state
          // from the name/code fields (Google Places gives us names, not our IDs).
          countryID: null,
          stateID: null,
        });
        if (ASSelectedClient) {
          ASSetSelectedClient({
            ...ASSelectedClient,
            InBodyAccount: inBodyValue,
          });
        }
        setInBodyDirty(false);
        // The saved fields are no longer "modified". In the client workspace
        // the personal-info refetch below happens to clearState() the slice;
        // the self-mode loader deliberately doesn't, and without this the save
        // bar stayed up after a successful save.
        dispatch(answersActions.clearPersonalInfoModifiedFields());
      }

      if (
        !checkHaveSameData(
          clientData.biometricQuest.myAnswers,
          clientData.biometricOldAnswers
        )
      ) {
        await EditBiometricQuestionnaire(
          clientData.biometricQuest.myAnswers,
          AuthUser.role
        );
      }
      // Re-read from the server so the form reflects what was actually saved.
      // Personal info first — in the client workspace its refetch clearState()s
      // the whole answers slice — then repopulate biometrics. The biometric
      // refetch goes through the resource registry with the caller's role so
      // it takes the right transport (see EditBiometricQuestionnaire) and the
      // cache/status stay in sync.
      if (isSelf) {
        await ASRefreshResource("personalInfo", subjectId, AuthUser.role);
      } else if (ASSelectedClient) {
        await ASGetCustomerPersonalInfo(ASSelectedClient.id, {
          deferClientSelection: true,
        });
      }
      await ASRefreshResource("biometricQuestionnaire", subjectId, AuthUser.role);
      setIsSendingData(false);
    } catch (e) {
      console.log("Error in saving data", e);
      const msg =
        (e as any)?.response?.data?.error ||
        (e as any)?.response?.data?.message ||
        (e as any)?.message ||
        "We couldn't save your changes. Please try again.";
      ASSetOpenSnackBar(true);
      ASSetSnackBarMessage({ title: "Save failed", message: msg });
      ASSetTypeSnackBar("alert");
      setIsSendingData(false);
    }
  };

  const handleCancel = async () => {
    dispatch(
      answersActions.updatePersonalInfoAnswers(
        clientData.personalInfoInitialAnswers
      )
    );
    dispatch(answersActions.clearPersonalInfoModifiedFields());
    // Drop the local InBody edit; the re-fetch below resets ASSelectedClient,
    // and the sync effect repopulates inBodyValue from it.
    setInBodyDirty(false);
    // Silent: a visible "loading" status would swap the tab body for the
    // PageLoader mid-cancel; the fields just snap back when the data lands.
    await ASRefreshResource("biometricQuestionnaire", subjectId, AuthUser.role, {
      silent: true,
    });
  };

  const value = useMemo<ProfileFormContextValue>(
    () => ({
      massUnit,
      onUnitChange,
      openPopperId,
      setOpenPopperId,
      inBodyValue,
      inBodyDirty,
      onInBodyChange,
      setAddressMissingParts,
      addressMissingParts,
      phoneInvalid,
      isBasicInformationDirty,
      isOnboardingDirty,
      dirtyTabs,
      isSendingData,
      isLoadingQuestionnaire:
        isResourcePending(biometricStatus) ||
        (isSelf && isResourcePending(personalInfoStatus)),
      handleSave,
      handleCancel,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      massUnit,
      openPopperId,
      inBodyValue,
      inBodyDirty,
      addressMissingParts,
      phoneInvalid,
      isBasicInformationDirty,
      isOnboardingDirty,
      dirtyTabs,
      isSendingData,
      biometricStatus,
      personalInfoStatus,
      isSelf,
      clientData,
      ASSelectedClient,
    ]
  );

  return (
    <ProfileFormContext.Provider value={value}>
      {children}
    </ProfileFormContext.Provider>
  );
};

export const useProfileForm = () => {
  const context = useContext(ProfileFormContext);
  if (!context) {
    throw new Error("useProfileForm must be used inside ProfileFormProvider");
  }
  return context;
};
